/**
 * Shared types of the Design Builder — the Figma-style canvas that sits beside
 * the DVM Layout Builder.
 *
 * Kept free of Angular imports on purpose (same rule as
 * `dvm-layout-builder.types.ts`): the canvas, the inspector, the persistence
 * service and any future published renderer all read these, and so can backend
 * codegen that only has the JSON.
 *
 * The document these types describe IS the save payload — what you see in the
 * right-hand JSON pane is exactly what is POSTed.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Nodes
// ─────────────────────────────────────────────────────────────────────────────

export type DesignNodeType =
  /** Container. Clips children, may run auto-layout, is a drop target. */
  | 'FRAME'
  /** Pure grouping — no clip, no own background; bounds = union of children. */
  | 'GROUP'
  | 'RECT'
  | 'ELLIPSE'
  | 'LINE'
  | 'TEXT'
  | 'IMAGE'
  /** A Lucide icon, stored by name and styled per node. */
  | 'ICON'
  /** Wraps one prebuilt DVM component (KPI, BAR, DRILL_DOWN_TABLE, …). */
  | 'WIDGET';

/** Node types that own a `children` array. */
export const CONTAINER_NODE_TYPES: readonly DesignNodeType[] = ['FRAME', 'GROUP'];

export type FillKind = 'none' | 'solid' | 'linear';

export interface GradientStop {
  /** 0..1 along the gradient axis. */
  offset: number;
  color: string;
}

export interface Fill {
  kind: FillKind;
  /** Used when `kind === 'solid'`. */
  color?: string;
  /** Used when `kind === 'linear'`. Degrees, 0 = left→right. */
  angle?: number;
  stops?: GradientStop[];
}

export type StrokeStyle = 'solid' | 'dashed' | 'dotted';

export interface Stroke {
  color: string;
  width: number;
  style: StrokeStyle;
  /** Colour opacity in % (0–100), Figma's stroke "%". Absent = 100. */
  opacity?: number;
}

export interface Shadow {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: string;
}

/** `[top, right, bottom, left]` — CSS order, so it reads the way it serialises. */
export type Edges = [number, number, number, number];

/** A single radius, or `[topLeft, topRight, bottomRight, bottomLeft]`. */
export type CornerRadius = number | [number, number, number, number];

export interface DesignNodeStyle {
  fill?: Fill;
  stroke?: Stroke | null;
  radius?: CornerRadius;
  shadow?: Shadow | null;
  /** Backdrop/layer blur in px. */
  blur?: number;
  padding?: Edges;
  overflow?: 'visible' | 'hidden';
}

export type AutoLayoutMode = 'none' | 'row' | 'column';
export type AutoLayoutAlign = 'start' | 'center' | 'end' | 'stretch';
export type AutoLayoutJustify = 'start' | 'center' | 'end' | 'between';

/**
 * Frame-only. `mode: 'none'` (the default) keeps children absolutely positioned
 * — pure design mode. Switching to row/column turns the frame into a flex
 * container, which is how a designed screen stays responsive once published.
 */
export interface AutoLayout {
  mode: AutoLayoutMode;
  gap: number;
  padding: Edges;
  align: AutoLayoutAlign;
  justify: AutoLayoutJustify;
  wrap: boolean;
}

export type NumberScale = 'none' | 'auto' | 'K' | 'M' | 'B';

/** A text node's link to one aggregated dataset column. */
export interface TextDataBinding {
  dataset: string;
  field: string;
  /** Backend aggregation code — the KPI card's list (SUM, AVG, COUNT, MIN, MAX…). */
  aggregation: string;
  /**
   * Set once the text is registered as a KPI component. Its value then comes
   * from `/components/compute`, like every chart, instead of being summarised
   * in the browser from a full `dynamic_filter` row dump.
   */
  componentId?: string;
  scale?: NumberScale;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /**
   * aggregation 'STATUS_COUNT' ("Count per status"): `field` is the group-by
   * column (e.g. planning_status); the STATUS_COUNTS API returns a count per
   * value and the text shows the one for `statusValue` (e.g. "planned").
   */
  statusValue?: string;
  /** Column counted per status (COUNT(<countField>)); default `uuid`. */
  countField?: string;
  /**
   * Only rows whose `filterField` matches count toward the value, e.g.
   * approval_status in [WAITING_FOR_APPROVAL] for "oldest pending".
   * `filterOperator`: as a dropdown's / table column's default filter - eq,
   * neq, contains, in (default: one of `filterValues`), empty, not_empty;
   * "null" as the value with eq / neq means empty / not empty.
   */
  filterField?: string;
  filterOperator?: string;
  filterValues?: string[];
}

/**
 * A text's value worked out in the browser from other layers, e.g.
 * `[Current Volumes] * [Price]` (see `design-formula.utils`). Shown through
 * `{{value}}` like a bound value, with the same number format.
 */
export interface TextFormula {
  expression: string;
  scale?: NumberScale;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextVerticalAlign = 'top' | 'middle' | 'bottom';

export interface TextContent {
  value: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  fontStyle?: 'normal' | 'italic';
  color: string;
  align: TextAlign;
  verticalAlign?: TextVerticalAlign;
  /** Multiplier, not px — survives font-size changes. */
  lineHeight: number;
  letterSpacing: number;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textDecoration?: 'none' | 'underline' | 'line-through';
}

/**
 * A Lucide icon. Stored **by name**, not as inlined SVG, so the document stays
 * small and restyling keeps working — `iconDataFor(name)` resolves the drawing
 * data at render time.
 */
export type IconAction = 'none' | 'info' | 'reset' | 'refresh' | 'filter' | 'search' | 'download' | 'upload' | 'share' | 'print';

export interface IconContent {
  /** PascalCase Lucide name, e.g. `'ChartBar'`. */
  name: string;
  /** Stroke colour. */
  color: string;
  /** Stroke width in Lucide's 24×24 user units (1–4 is the useful range). */
  strokeWidth: number;
  /**
   * Keep the stroke visually constant as the icon scales. Off (Lucide's
   * default) means the stroke scales with the icon, which is usually what you
   * want for a large decorative glyph; on keeps hairlines crisp at big sizes.
   */
  absoluteStrokeWidth?: boolean;
  /** Optional fill behind the strokes. `'none'` (the default) leaves it open. */
  fill?: string;
  /** Runs in Preview and published dashboards; omitted means decorative. */
  action?: IconAction;
  /** FILE_UPLOAD widget node used by the upload action. */
  targetNodeId?: string;
  info?: {
    source: 'static' | 'api';
    title: string;
    text: string;
    url: string;
    /** Optional dot-separated path to text in the GET response. */
    responsePath: string;
  };
}

export interface ImageContent {
  /** URL or data URI. */
  src: string;
  fit: 'cover' | 'contain' | 'fill' | 'none';
  alt?: string;
}

/** Where a widget node's data comes from, so the builder can re-fetch it. */
export interface WidgetBinding {
  dataset?: string;
  field?: string;
  aggregation?: string;
  /** Free-form extras a specific layoutType needs (category column, measures…). */
  [key: string]: unknown;
}

/**
 * The bridge to everything the DVM Layout Builder already produces. `data` is
 * the SAME `[data]` object the shared component already accepts — identical in
 * shape to `DashboardTile.kpiData` / `barData` / `drillData` / … so a widget
 * can be moved between the two builders without translation.
 */
export interface WidgetContent {
  /** One of `options.layoutTypes` in `dvm-layout-builder.json`. */
  layoutType: string;
  data: Record<string, unknown> | null;
  /**
   * **Design-tier** settings: how the chart LOOKS, independent of any dataset —
   * variant (stacked / grouped / 100% …), orientation, and every format control
   * (palette, legend, labels, tooltip). Chosen while designing, so the canvas
   * can show a stacked bar as an actually-stacked bar before data exists.
   *
   * Deliberately separate from {@link binding}, which is the **data tier**
   * (dataset, category, measures) filled in later. Keeping the two apart means
   * the mapping work never has to untangle appearance keys from data keys.
   */
  appearance?: Record<string, unknown>;
  binding?: WidgetBinding;
  /** Per-widget format overrides — mirrors `DashboardTile.format`. */
  format?: Record<string, unknown>;
  /** Column this widget publishes when a mark is clicked. */
  filterKey?: string;
  /** Node ids that react to this widget's cross-filter. */
  listenerNodeIds?: string[];
  /** Stable component id carried over from the Layout Builder, when any. */
  componentId?: string;
  /**
   * True while `data` is generated placeholder data (see `design-sample-data`),
   * so the widget renders as a real chart on drop. Binding a dataset clears it.
   * The publish payload strips sample data — it must never reach production.
   */
  sample?: boolean;
  /**
   * The DVM Layout Builder build this widget's data came from: the generated
   * tile (payload, format, componentId, control config) minus its render data.
   * Re-selecting the widget loads it back into the Data tab's build form.
   */
  build?: Record<string, unknown>;
}

/**
 * Slides a node between marker nodes by value. Its number and each stop's
 * number are read from their texts — the live value when bound, otherwise the
 * typed number.
 */
/** One "Color by value" range: From / To included; an empty side has no limit. */
export interface ValueColorRange {
  from?: number | null;
  to?: number | null;
  /** A marker on the canvas (a sibling text, e.g. MFN) whose live number is From - wins over `from`. */
  fromId?: string;
  /** Same for To (e.g. FLOOR). */
  toId?: string;
  color: string;
}

/** What "Color by value" colours: the text, or the pill (shape) right behind it - the text then turns white. */
export type ValueColorTarget = 'text' | 'pill';

export interface SlideBinding {
  /** Sibling nodes (same parent) the node moves between. */
  stopIds: string[];
  /** Sibling layers that move along with it, by the same distance (its label,
   *  its % text ...). */
  followerIds?: string[];
  /** Landing on a stop (stops never move): slide to the nearest free spot
   *  (default), go beside it, above / below it, or overlap. */
  avoidOverlap?: SlideOverlapMode;
}

export type SlideOverlapMode = 'nearest' | 'beside' | 'above' | 'below' | 'none';

/**
 * "Show at levels": the layer shows only while a zone slider's handle is in one
 * of these levels (its blocks, by label) - e.g. a "Level 2 triggered" message.
 */
/**
 * "Text by level": the text shows the message typed for the zone slider's
 * current level (by the level's label); a level left blank shows the text itself.
 */
export interface LevelTexts {
  /** The zone slider; blank = the first one on the design. */
  sliderId?: string;
  texts: Record<string, string>;
}

export interface LevelVisibility {
  /** The zone slider; blank = the first one on the design. */
  sliderId?: string;
  levels: string[];
}

export interface DesignNode {
  id: string;
  name: string;
  type: DesignNodeType;
  /** px, relative to the PARENT's content box (Figma semantics). */
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * Cumulative visual scale inherited from GROUP resizes. Geometry still uses
   * width/height, while these factors let fixed-size widget internals scale as
   * one composition instead of overflowing their new child bounds.
   */
  groupScaleX?: number;
  groupScaleY?: number;
  /** Degrees, clockwise, about the node's centre. */
  rotation: number;
  visible: boolean;
  locked: boolean;
  /** 0..1 */
  opacity: number;
  style: DesignNodeStyle;
  /** FRAME only. */
  children?: DesignNode[];
  /**
   * GROUP only: the nodes it holds together, by id.
   *
   * A group REFERENCES its members instead of owning them — they stay direct
   * children of the screen, in the band their own kind belongs to, and the
   * group is just a handle that moves and resizes them as one. It draws
   * nothing; its box is derived from the members on every commit.
   */
  memberIds?: string[];
  /** Screen FRAME only: its layers, back to front. */
  layers?: DesignLayer[];
  /** Direct child of a screen FRAME only: the layer it is painted in. */
  layerId?: string;
  /** FRAME only. */
  layout?: AutoLayout;
  /**
   * FRAME only. The frame is a **dashboard screen**: its width tracks the
   * artboard rather than being a fixed number, and the published page fills its
   * container. This is the default for a Screen frame — a dashboard has no
   * single true width, so pinning one would only look right on one monitor.
   */
  fluid?: boolean;
  /**
   * Root FRAME only: made as a button's popup screen ("Create popup screen"). It stays a
   * free box (moves, resizes, no screen tab) even while the button's link to it is not
   * saved yet - e.g. a table button's, which lives in the table's unsaved Data-tab form.
   */
  popupScreen?: boolean;
  /**
   * Screen FRAME only. The size the author set in the Screen panel; a missing
   * side is automatic (width = artboard, height = down to the content). A set
   * height never cuts content off — the page still grows to fit it.
   */
  screenSize?: { width?: number; height?: number };
  /** TEXT only. */
  text?: TextContent;
  /**
   * TEXT only. Binds the text to a backend value: `{{value}}` in `text.value`
   * is replaced with the aggregated column (a text without the token shows
   * the value alone).
   */
  dataBinding?: TextDataBinding;
  /** TEXT only. A front-end calculation over other layers; wins over `dataBinding`. */
  formula?: TextFormula;
  /**
   * TEXT only. "Color by value": the text takes the colour of the first range
   * its live number falls in (see `design-value-color.utils`).
   */
  valueColors?: ValueColorRange[];
  /**
   * "Move with value": in Preview and published screens the node slides
   * between its stops by its own number (see `design-slide.utils`).
   */
  slide?: SlideBinding;
  /** "Color by value": colour the text (default) or the pill behind it. */
  valueColorTarget?: ValueColorTarget;
  /** "Color by value": compare another text's number (a sibling, e.g. the price) instead of its own - so a
   *  margin label takes exactly the price's colour. */
  valueColorSourceId?: string;
  /** "Initials of": show the initials of another text's value (e.g. an avatar's "TV" for a user's
   *  name or email) - they follow that text as its value changes. */
  initialsOfId?: string;
  /** Shown only at these zone-slider levels (see LevelVisibility). */
  showAtLevels?: LevelVisibility;
  /** A message per zone-slider level (see LevelTexts). */
  levelTexts?: LevelTexts;
  /** IMAGE only. */
  image?: ImageContent;
  /** ICON only. */
  icon?: IconContent;
  /** WIDGET only. */
  widget?: WidgetContent;
}

// ─────────────────────────────────────────────────────────────────────────────
// Document
// ─────────────────────────────────────────────────────────────────────────────

export interface DesignGrid {
  size: number;
  snap: boolean;
  visible: boolean;
}

export interface DesignCanvasSettings {
  /** Artboard size in px. */
  width: number;
  height: number;
  background: string;
  grid: DesignGrid;
  /**
   * Re-read every widget's data from the database this often (seconds) while
   * the dashboard is shown in Preview or published. Absent = only on open.
   * See `design-refresh.utils.ts`.
   */
  autoRefreshSeconds?: number;
  /**
   * Canvas filters (Design tab, nothing selected → Canvas filters): each one
   * filters only the widgets and texts it targets. See `design-canvas-filter.utils.ts`.
   */
  filters?: CanvasFilter[];
}

/**
 * One canvas-level filter: a column of an entity (dataset) tested with an
 * operator, applied to the widgets and texts it targets - every request that
 * loads them (open, refresh, dropdown / input recompute) carries it.
 */
export interface CanvasFilter {
  id: string;
  /** The entity (dataset) the column is picked from. */
  entity: string;
  field: string;
  /** eq / neq / contains / in / empty / not_empty - as a dropdown's option filter. */
  operator: string;
  /** The value(s) tested; `in` uses them all, eq / neq / contains the first. */
  values: string[];
  /** Node ids of the widgets and texts it filters. */
  targets: string[];
  /** Kept on the canvas but not applied. */
  disabled?: boolean;
}

export interface DesignPage {
  id: string;
  name: string;
  nodes: DesignNode[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Layers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The fixed set of layer kinds — see docs/DESIGN_BUILDER.md §4 Layers.
 *
 * Closed on purpose: each kind is one JSON column in the planned
 * `design_screen` table, so the set is schema, not a free-form label.
 */
export type DesignLayerKind =
  | 'BACKGROUND'
  | 'SHAPES'
  | 'COMPONENT'
  | 'CONTROLS'
  | 'TEXT'
  | 'OVERLAY'
  /** Groups. Holds references only — the members stay in their own bands. */
  | 'GROUP';

/**
 * One band of a screen. A layer owns no nodes of its own: the screen's children
 * carry `layerId`, and the children array is kept ordered so each layer's nodes
 * are contiguous — that way the array stays the single source of paint order
 * and no renderer has to learn about layers.
 */
export interface DesignLayer {
  id: string;
  name: string;
  kind: DesignLayerKind;
}

export type DesignStatus = 'draft' | 'published';

export interface DesignMeta {
  createdAt: string;
  updatedAt: string;
  author?: string;
  status: DesignStatus;
  publishedAt?: string;
}

/**
 * A project groups several dashboards.
 *
 * The parent above a design: one project ("Sales FY25") owns the three or four
 * dashboards built for it, so they can be listed, permissioned and published
 * together instead of living in one flat pile keyed only by dashboard id.
 */
export interface DesignProject {
  id: string;
  name: string;
  slug: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DesignDocument {
  schemaVersion: 1;
  id: string;
  name: string;
  slug: string;
  /** Owning project. Absent means the design has not been filed under one yet. */
  projectId?: string;
  /** Denormalised for listings, so a name can be shown without a second fetch. */
  projectName?: string;
  canvas: DesignCanvasSettings;
  pages: DesignPage[];
  /** Optional design tokens (colours, fonts) reusable across nodes. */
  theme?: { tokens: Record<string, string> };
  meta: DesignMeta;
}

/** Row shape returned by the designs list endpoint / localStorage fallback. */
export interface DesignSummary {
  id: string;
  name: string;
  slug: string;
  status: DesignStatus;
  updatedAt: string;
  projectId?: string;
  projectName?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Palette
// ─────────────────────────────────────────────────────────────────────────────

export type PaletteGroup = 'frames' | 'design' | 'charts' | 'inputs';

/**
 * One draggable entry in the left-hand palette. `create` returns the node to
 * insert — a function so every drop gets a fresh object with a fresh id.
 */
export interface PaletteItem {
  key: string;
  label: string;
  /** Material icon name. */
  icon: string;
  group: PaletteGroup;
  hint?: string;
  nodeType: DesignNodeType;
  /** Set for `nodeType: 'WIDGET'`. */
  layoutType?: string;
  /** Default size dropped on the canvas. */
  defaultWidth: number;
  defaultHeight: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Canvas / interaction
// ─────────────────────────────────────────────────────────────────────────────

export type DesignTool =
  | 'select'
  | 'hand'
  | 'frame'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'text'
  | 'image';

/** The eight resize handles plus the rotate grip. */
export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** A snap guide drawn while dragging, in canvas (document) coordinates. */
export interface AlignmentGuide {
  axis: 'x' | 'y';
  /** Position along the axis. */
  at: number;
  /** Span of the guide line, so it only spans the involved nodes. */
  from: number;
  to: number;
}

export interface MarqueeRect extends Rect {
  active: boolean;
}

/** MIME type for palette → canvas drags. */
export const DESIGN_PALETTE_DRAG_TYPE = 'application/x-dvm-design-palette';
/** MIME type for icon-library → canvas drags. Carries the Lucide icon name. */
export const DESIGN_ICON_DRAG_TYPE = 'application/x-dvm-design-icon';
/** MIME type for layers-tree reorder drags. */
export const DESIGN_LAYER_DRAG_TYPE = 'application/x-dvm-design-layer';
